// Complete replacement asset. Uses the original cart-total eligibility rule.
//
// Speed notes
// 1. Every gift mutation asks Shopify for the cart drawer sections in the same
//    response (Cart API "sections" parameter), so the drawer is rendered from
//    that response instead of a separate refresh request.
// 2. update.js and change.js already return the full cart, so that response is
//    reused instead of fetching cart.js again after every step.
// 3. The cart JSON returned by the theme's own change/update requests is
//    cached, so the sync that runs straight after a quantity change starts
//    from it instead of fetching cart.js. The cache is dropped as soon as any
//    other cart request happens, or when the window regains focus.
(() => {
  if (window.cartGiftController) return;
  class CartGiftController {
    constructor() {
      this.mutationSeq = 0;
      this.lastCart = null;
      this.retries = 0;
    }

    isBusy() {
      return Boolean(this.intent || this.requests || this.running || this.pending || this.timer || this.failed);
    }

    updateNavigation() {
      const busy = this.isBusy();
      document.querySelectorAll('cart-drawer a[href], cart-drawer button[name="checkout"]').forEach(element => {
        if (element.tagName === 'A' && !/\/cart\/?$/.test(new URL(element.href, location.href).pathname)) return;
        if (busy) {
          if (!element.hasAttribute('data-gift-navigation-lock')) {
            element.dataset.giftNavigationLock = element.getAttribute('aria-disabled') || '';
          }
          element.setAttribute('aria-disabled', 'true');
          element.setAttribute('aria-busy', 'true');
        } else if (element.hasAttribute('data-gift-navigation-lock')) {
          const previous = element.dataset.giftNavigationLock;
          if (previous) element.setAttribute('aria-disabled', previous);
          else element.removeAttribute('aria-disabled');
          element.removeAttribute('aria-busy');
          delete element.dataset.giftNavigationLock;
        }
      });
    }

    readConfig() {
      const element = document.querySelector('drawer-gifts');
      const fallback = document.getElementById('cart-gifts-config');
      const config = JSON.parse(element?.dataset.config || fallback?.textContent || '{"data":{"gifts":[]}}').data;
      return { gifts: (config.gifts || []).map(gift => ({
        ...gift,
        price: Number(gift.price) * 100,
        product_id: Number(gift.product_id),
        variant_ids: (gift.variant_ids || [gift.product_id]).map(Number)
      })).filter(gift => gift.price > 0 && gift.product_id > 0) };
    }

    /** Section ids the cart drawer renders, or [] when there is no drawer (cart page). */
    sectionIds() {
      const drawer = document.querySelector('cart-drawer');
      try {
        return drawer?.getSectionsToRender?.().map(section => section.section) || [];
      } catch (_) {
        return [];
      }
    }

    static isCart(data) {
      return Boolean(data && Array.isArray(data.items) && typeof data.total_price === 'number');
    }

    async request(path, body) {
      const root = window.Shopify?.routes?.root || '/';
      let payload = body;
      if (body) {
        // Our own mutation: any cached cart is now out of date.
        this.lastCart = null;
        const ids = this.sectionIds();
        if (ids.length) payload = { ...body, sections: ids.join(','), sections_url: location.pathname };
      }
      const response = await (this.originalFetch || window.fetch.bind(window))(`${root}${path}`, payload ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your gift.');
      return data;
    }

    /** Fresh cart from the theme's last request, if nothing has changed since. */
    takeFreshCart() {
      const cached = this.lastCart;
      if (!cached || cached.seq !== this.mutationSeq || Date.now() - cached.at > 4000) return null;
      return cached.data;
    }

    rememberCart(data) {
      this.lastCart = { data, seq: this.mutationSeq, at: Date.now() };
    }

    /** Render the drawer from sections returned with a mutation, else fall back to a refresh. */
    async render(sections) {
      const drawer = document.querySelector('cart-drawer');
      const ids = this.sectionIds();
      if (drawer?.renderContents && sections && ids.length && ids.every(id => sections[id])) {
        try {
          await drawer.renderContents({ sections }, false);
          customElements.get('cart-drawer')?.recalculateCssVarHeights?.();
          return;
        } catch (_) {
          // fall through to a normal refresh
        }
      }
      const items = document.querySelector('cart-drawer cart-items') || document.querySelector('cart-items');
      if (!items?.refresh) throw new Error('Cart refresh is unavailable');
      await items.refresh();
    }

    async sync() {
      const config = this.readConfig();
      let cart = this.takeFreshCart() || await this.request('cart.js');
      let changed = false;
      let lastSections = null;
      // Re-check the cart after every mutation. Both rewards settle before rendering.
      // A bound avoids endless retries if external cart logic conflicts with gifts.
      for (let pass = 0; pass < 12; pass += 1) {
        let action = null;
        for (const gift of config.gifts) {
          const isGiftVariant = (item) => gift.variant_ids.includes(Number(item.variant_id || item.id));
          const matches = (cart.items || []).filter((item) =>
            isGiftVariant(item) && item.properties?.Gift === 'Free Gift');
          const paid = (cart.items || []).filter((item) =>
            isGiftVariant(item) && item.properties?.Gift !== 'Free Gift');
          const quantity = matches.reduce((sum, item) => sum + item.quantity, 0);
          const eligible = cart.total_price >= gift.price;
          if (!eligible && quantity) {
            // Remove the gift. If it was converted from a paid pair, give that pair back as paid.
            const restore = matches.filter((item) => item.properties?._from_paid);
            const remove = ['cart/update.js', { updates: Object.fromEntries(matches.map((item) => [item.key, 0])) }];
            action = restore.length
              ? [remove, ['cart/add.js', { items: restore.map((item) => ({ id: Number(item.variant_id || item.id), quantity: item.quantity })) }]]
              : remove;
          } else if (quantity > 1) {
            action = ['cart/update.js', { updates: Object.fromEntries(matches.map((item, index) => [item.key, index === 0 ? 1 : 0])) }];
          } else if (eligible && !quantity) {
            // A paid pair is already in the cart: turn one into the free gift,
            // but only if the cart still qualifies once that pair is free.
            const convertible = paid.find((item) => cart.total_price - item.final_price >= gift.price);
            if (convertible) {
              action = [
                ['cart/change.js', { id: convertible.key, quantity: convertible.quantity - 1 }],
                ['cart/add.js', { items: [{ id: Number(convertible.variant_id || convertible.id), quantity: 1, properties: { Gift: 'Free Gift', _from_paid: '1' } }] }]
              ];
            } else if (gift.auto_add_available !== false && gift.variant_ids.length === 1) {
              action = ['cart/add.js', { items: [{ id: gift.product_id, quantity: 1, properties: { Gift: 'Free Gift' } }] }];
            }
          }
          if (action) break;
        }

        if (!action) {
          this.rememberCart(cart);
          if (changed) await this.render(lastSections);
          document.querySelector('.reward-gift-error')?.remove();
          return;
        }

        const steps = Array.isArray(action[0]) ? action : [action];
        document.getElementById('cart-drawer')?.classList.add('dhs-cart-busy');
        let nextCart = null;
        for (const step of steps) {
          const data = await this.request(...step);
          lastSections = data.sections || null;
          // update.js / change.js return the whole cart; add.js does not.
          nextCart = CartGiftController.isCart(data) ? data : null;
        }
        changed = true;
        cart = nextCart || await this.request('cart.js');
      }
      throw new Error('Gift updates did not settle');
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.timer = null; this.run(); }, 80);
      this.updateNavigation();
    }

    async run() {
      this.pending = true;
      if (this.requests || this.intent) { this.updateNavigation(); return; }
      if (this.running) return;
      this.running = true;
      this.failed = false;
      this.updateNavigation();
      try {
        while (this.pending) {
          this.pending = false;
          await this.sync();
          if (this.requests || this.intent) break;
        }
        this.retries = 0;
      } catch (error) {
        console.error('Gift sync failed:', error);
        this.lastCart = null;
        // Usually the cart changed underneath us (a line was already removed).
        // Start again from a fresh cart a couple of times before giving up.
        if (this.retries < 2) {
          this.retries += 1;
          this.running = false;
          document.getElementById('cart-drawer')?.classList.remove('dhs-cart-busy');
          this.schedule();
          return;
        }
        this.retries = 0;
        this.failed = true;
        const host = document.querySelector('drawer-gifts') || document.querySelector('cart-drawer');
        if (host && !host.querySelector('.reward-gift-error')) {
          const message = document.createElement('p');
          message.className = 'reward-gift-error';
          message.setAttribute('role', 'alert');
          message.textContent = 'We couldn’t update your free gift. Please refresh the page to try again.';
          host.appendChild(message);
        }
      } finally {
        this.running = false;
        document.getElementById('cart-drawer')?.classList.remove('dhs-cart-busy');
        this.updateNavigation();
      }
    }

    start() {
      const schedule = () => this.schedule();
      const style = document.createElement('style');
      // Links fade while locked; the checkout button shows its own Updating cart state.
      style.textContent = 'cart-drawer a[data-gift-navigation-lock] { opacity:.45!important; cursor:wait!important; } cart-drawer button[data-gift-navigation-lock] { cursor:wait!important; } .reward-gift-error { margin:10px 20px 0; font-size:12px; line-height:1.4; color:#FF6B6B; }';
      document.head.appendChild(style);
      const block = event => {
        const control = event.target.closest?.('cart-drawer a[href], cart-drawer button[name="checkout"]');
        if (!control || !this.isBusy()) return;
        if (control.tagName === 'A' && !/\/cart\/?$/.test(new URL(control.href, location.href).pathname)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      document.addEventListener('click', block, true);
      document.addEventListener('auxclick', block, true);
      document.addEventListener('submit', event => {
        if (event.target.matches?.('#cart-drawer-form') && this.isBusy()) {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      }, true);
      // Lock before the theme's debounced quantity handler starts its request.
      document.addEventListener('change', event => {
        if (event.target.matches?.('cart-items [name="updates[]"]')) {
          this.intent = true; this.updateNavigation();
        }
      }, true);
      // Track add/change/update requests made by the theme; our own gift
      // requests use originalFetch and remain covered by the running lock.
      this.originalFetch = window.fetch.bind(window);
      window.fetch = (...args) => {
        let mutation = false;
        try {
          const path = new URL(typeof args[0] === 'string' || args[0] instanceof URL ? args[0] : args[0].url, location.href).pathname;
          mutation = /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(path);
        } catch (_) {}
        if (!mutation) return this.originalFetch(...args);
        this.intent = false;
        this.requests = (this.requests || 0) + 1;
        this.mutationSeq += 1;
        const seq = this.mutationSeq;
        this.lastCart = null;
        this.updateNavigation();
        return this.originalFetch(...args)
          .then(response => {
            // Keep the cart the theme just got back, so the next sync can skip cart.js.
            if (response.ok) {
              response.clone().json().then(data => {
                if (seq === this.mutationSeq && CartGiftController.isCart(data)) {
                  this.lastCart = { data, seq, at: Date.now() };
                }
              }).catch(() => {});
            }
            return response;
          })
          .finally(() => {
            this.requests -= 1;
            schedule();
          });
      };
      ['on:cart:add', 'on:line-item:change', 'shopify:section:load'].forEach(name => document.addEventListener(name, schedule, true));
      // The cart may have changed in another tab, so start from a fresh cart.
      const freshSchedule = () => { this.lastCart = null; schedule(); };
      window.addEventListener('pageshow', freshSchedule);
      window.addEventListener('focus', freshSchedule);
      const bind = () => {
        document.querySelectorAll('cart-items').forEach(items => {
          if (this.bound.has(items)) return;
          this.bound.add(items);
          // Original theme dispatches this event on the cart-items element.
          items.addEventListener('on:line-item:change', schedule);
        });
      };
      this.bound = new WeakSet();
      this.observer = new MutationObserver(records => {
        bind();
        this.updateNavigation();
        if (records.some(record => record.target.closest?.('cart-drawer, drawer-gifts, cart-items') ||
          Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('cart-drawer, drawer-gifts, cart-items') || node.querySelector('drawer-gifts'))))) schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      bind();
      schedule();
    }
  }
  window.cartGiftController = new CartGiftController();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.cartGiftController.start(), { once: true });
  else window.cartGiftController.start();
})();